import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Command } from "@/shared/lib/commands";
import { useRegisterCommands } from "@/shared/lib/hooks/useCommands";
import { useConversations } from "../hooks/useConversations";

/** How many recent conversations the ⌘K palette surfaces. */
const MAX_PALETTE_CONVERSATIONS = 12;

/**
 * Registers the user's recent conversations as ⌘K palette commands (navigate to
 * `/chat/$id`), so the palette finds chats by title alongside the app actions.
 * Renders nothing — mount it once inside the auth-gated shell (composed in
 * `app`, like the sidebar's ConversationList). Reads the same unfiltered list
 * query the sidebar uses, so it costs no extra fetch.
 */
export function ConversationCommands() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useConversations();

  const commands = useMemo<Command[]>(() => {
    const group = t("commandPalette.recentChats");
    return (data?.items ?? []).slice(0, MAX_PALETTE_CONVERSATIONS).map((conversation) => ({
      id: `conversation:${conversation.id}`,
      title: conversation.title || t("commandPalette.untitled"),
      group,
      perform: () => void navigate({ to: "/chat/$id", params: { id: conversation.id } }),
    }));
  }, [data, navigate, t]);

  useRegisterCommands(commands);
  return null;
}
